import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '@/services/api';
import { forgotPasswordSchema, formatZodErrors } from '@/shared/validation';
import { FormError } from '@/shared/FormError';

/**
 * Richiesta reset password: chiede l'email e risponde SEMPRE con lo stesso
 * messaggio (il backend non rivela se l'email esiste — anti enumeration).
 */
export function ForgotPasswordPage(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const parsed = forgotPasswordSchema.safeParse({ email });
    if (!parsed.success) {
      setFieldErrors(formatZodErrors(parsed));
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/forgot-password', parsed.data);
      setSent(true);
    } catch {
      // 429 (rate limit IP) o errore rete: messaggio generico, nessun dettaglio.
      setError('Richiesta non riuscita. Riprova tra qualche minuto.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-shell">
      <div className="login-container">
        <div className="login-logo">
          <img src="/icon-128.webp" alt="VVF"
            style={{ width: 72, height: 72, margin: '0 auto 10px', display: 'block' }} />
          <h1>VVF Tracker</h1>
          <p>Recupero password</p>
        </div>

        <div className="login-form-wrap">
          <h2>Password dimenticata</h2>

          {sent ? (
            <>
              <div style={{ background: '#d4edda', color: '#155724', padding: 14, borderRadius: 8, fontSize: 14, marginBottom: 16 }}>
                ✉️ Se l'email esiste, riceverai un messaggio con il link per
                reimpostare la password. Il link scade tra 30 minuti —
                controlla anche lo spam.
              </div>
              <div className="form-footer">
                <Link to="/login">← Torna al login</Link>
              </div>
            </>
          ) : (
            <>
              <p style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
                Inserisci l'email del tuo account: ti invieremo un link per
                scegliere una nuova password.
              </p>
              {error && <div className="error-message">{error}</div>}

              <form onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input id="email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} autoFocus />
                  <FormError message={fieldErrors.email} />
                </div>

                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? 'Invio in corso...' : 'Invia link di reset'}
                </button>
              </form>

              <div className="form-footer">
                <Link to="/login">← Torna al login</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
